// Semana — página dedicada ao tema semanal (Fase B, spec Carlos).
// Consome exclusivamente useDaily().weekly_theme (fonte oficial da landing).
//
// Estrutura:
//  - Header discreto com voltar
//  - Hero: eyebrow "Esta semana" + título theme_name em navy + verso âncora
//  - Objetivo espiritual (parágrafo em itálico)
//  - Descrição/breve explicação (theme_description)
//  - Grid de conteúdos alinhados:
//     · Devocional de hoje (daily.devotional)
//     · Oração de hoje (daily.prayer)
//     · Sermão recomendado (daily.sermon)
//     · Plano recomendado (daily.related_plan)
//  - CTA "Começar o devocional de hoje" → /devocional
//
// Tom: pastoral, limpo, reverente. Zero linguagem promocional.
// Se weekly_theme null → placeholder gracioso com link para o devocional.

import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, ArrowRight, BookOpen, MessagesSquare, Radio, Route,
  CalendarDays, Bookmark, Target, Users,
} from "lucide-react";
import { BibleRefLink } from "../lib/bibleRef";
import useDaily from "../lib/useDaily";
import PrayerWeekCard from "../components/PrayerWeekCard";
import { WeeklyThemeDaysList } from "../components/WeeklyThemeDaysList";
import { LiturgicalBadge } from "../components/LiturgicalBadge";
import { Skeleton } from "../components/ui/skeleton";

const DEFAULT_GOLD = "#C9A961";

function tint(hex, alpha) {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return null;
  const h = hex.replace("#", "");
  const bigint = parseInt(
    h.length === 3 ? h.split("").map((c) => c + c).join("") : h,
    16,
  );
  if (Number.isNaN(bigint)) return null;
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-gold/12 flex items-center justify-center">
          <Icon className="w-4 h-4 text-gold" />
        </span>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{title}</p>
        <span className="flex-1 h-px bg-border" />
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function Semana() {
  const { daily, loading } = useDaily();
  const wt = daily?.weekly_theme || null;
  const devotional = daily?.devotional || null;
  const sermon = daily?.sermon || null;
  const plan = daily?.related_plan || null;
  const prayer = (daily?.prayer || "").trim();
  // Fase 2 (iter58) — campos do Calendário Pastoral (defensivos)
  const todosOsDias = Array.isArray(daily?.todos_os_dias) ? daily.todos_os_dias : [];
  const versiculosMemoria = Array.isArray(daily?.versiculos_memoria) ? daily.versiculos_memoria : [];
  const perguntasGrupo = Array.isArray(daily?.perguntas_grupo) ? daily.perguntas_grupo : [];
  const acaoMissionaria = (daily?.acao_missionaria || "").trim();
  const todayIso = (daily?.date || "").trim();

  const accent = wt?.accent_color || DEFAULT_GOLD;
  const heroBg = tint(accent, 0.08) || "rgba(201, 169, 97, 0.08)";
  const heroBorder = tint(accent, 0.35) || `${DEFAULT_GOLD}59`;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-24">
      <Link
        to="/"
        data-testid="semana-back"
        className="inline-flex items-center gap-1.5 text-xs text-navy/60 hover:text-gold"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>

      {loading && !wt && (
        <div className="mt-6">
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-40 w-full rounded-2xl mt-6" />
        </div>
      )}

      {!loading && !wt && (
        <div
          data-testid="semana-placeholder"
          className="mt-6 rounded-2xl border border-gold/30 bg-[hsl(var(--parchment))] px-5 py-6 sm:p-8"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold">
            <Sparkles className="w-3 h-3" /> Esta semana
          </div>
          <h1 className="mt-2 font-display text-2xl sm:text-3xl text-navy leading-tight">
            Semana de Oração
          </h1>
          <p className="mt-3 text-sm text-navy/70 leading-relaxed">
            Em breve, o tema semanal será publicado pelo pastor Carlos.
            Enquanto isso, continue com o devocional de hoje.
          </p>
          <Link
            to="/devocional"
            data-testid="semana-placeholder-cta"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 text-[11px] uppercase tracking-widest transition-colors"
          >
            Devocional de hoje <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {wt && (
        <>
          {/* Hero */}
          <section
            data-testid="semana-hero"
            data-theme={wt.theme_slug || "oracao"}
            className="mt-6 rounded-2xl border px-5 py-6 sm:px-7 sm:py-8"
            style={{ background: heroBg, borderColor: heroBorder }}
          >
            <div className="flex flex-wrap items-center gap-2">
              <div
                className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em]"
                style={{ color: accent }}
              >
                <Sparkles className="w-3 h-3" /> Esta semana
              </div>
              <LiturgicalBadge weeklyTheme={wt} testid="semana-liturgical-badge" showThemeName={false} />
            </div>
            <h1
              className="mt-2 font-display text-3xl sm:text-4xl text-navy leading-[1.1]"
              data-testid="semana-title"
            >
              {wt.theme_name}
            </h1>

            {wt.verse_text && (
              <div className="mt-4 pt-4 border-t border-navy/8">
                <p
                  className="font-display text-lg sm:text-xl text-navy/90 leading-relaxed italic"
                  data-testid="semana-verse"
                >
                  &ldquo;{wt.verse_text}&rdquo;
                </p>
                {wt.verse_reference && (
                  <BibleRefLink
                    refText={wt.verse_reference}
                    className="mt-1.5 inline-block text-[11px] uppercase tracking-widest hover:underline"
                    style={{ color: accent }}
                    testid="semana-verse-ref"
                  >
                    {wt.verse_reference}
                  </BibleRefLink>
                )}
              </div>
            )}

            {wt.objetivo_espiritual && (
              <div className="mt-5" data-testid="semana-objective">
                <p className="text-[10px] uppercase tracking-widest text-navy/50">
                  Objetivo espiritual
                </p>
                <p className="mt-1.5 text-sm text-navy/80 leading-relaxed">
                  {wt.objetivo_espiritual}
                </p>
              </div>
            )}

            {wt.theme_description && wt.theme_description !== wt.objetivo_espiritual && (
              <p
                className="mt-5 text-sm text-navy/75 leading-relaxed whitespace-pre-line"
                data-testid="semana-description"
              >
                {wt.theme_description}
              </p>
            )}
          </section>

          {/* Conteúdos alinhados ao tema */}
          {devotional?.title && (
            <Section title="Devocional de hoje" icon={BookOpen} testid="semana-devotional-section">
              <Link
                to="/devocional"
                data-testid="semana-devotional-card"
                className="block rounded-2xl bg-white border border-border p-5 sm:p-6 hover:border-gold/60 hover:shadow-sm transition-all group"
              >
                <p className="font-display text-xl sm:text-2xl text-navy leading-snug group-hover:text-gold transition-colors">
                  {devotional.title}
                </p>
                {devotional.lead_paragraph && (
                  <p className="mt-2 text-sm text-navy/70 leading-relaxed line-clamp-3">
                    {devotional.lead_paragraph}
                  </p>
                )}
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-gold group-hover:gap-2.5 transition-all">
                  Ler devocional <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            </Section>
          )}

          {prayer && (
            <Section title="Oração de hoje" icon={MessagesSquare} testid="semana-prayer-section">
              <div className="rounded-2xl bg-[hsl(var(--parchment))] border border-navy/10 p-5 sm:p-6">
                <p
                  className="font-display text-navy/90 leading-relaxed text-lg italic"
                  data-testid="semana-prayer-text"
                >
                  {prayer}
                </p>
              </div>
            </Section>
          )}

          {sermon?.title && sermon?.slug && (
            <Section title="Sermão recomendado" icon={Radio} testid="semana-sermon-section">
              <Link
                to={`/sermoes/${sermon.slug}`}
                data-testid="semana-sermon-card"
                className="block rounded-2xl bg-white border border-border p-5 hover:border-gold/60 hover:shadow-sm transition-all group"
              >
                {sermon.category && (
                  <p className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                    {sermon.category}
                  </p>
                )}
                <p className="mt-1 font-display text-lg sm:text-xl text-navy leading-snug group-hover:text-gold transition-colors">
                  {sermon.title}
                </p>
                {sermon.verse_reference && (
                  <p className="mt-1.5 text-[11px] text-gold uppercase tracking-widest">
                    {sermon.verse_reference}
                  </p>
                )}
              </Link>
            </Section>
          )}

          {plan?.title && plan?.slug && (
            <Section title="Plano recomendado" icon={Route} testid="semana-plan-section">
              <Link
                to={`/planos/${plan.slug}`}
                data-testid="semana-plan-card"
                className="block rounded-2xl bg-white border border-border p-5 hover:border-gold/60 hover:shadow-sm transition-all group"
              >
                {plan.category && (
                  <p className="text-[10px] uppercase tracking-[0.25em] text-navy/50">
                    {plan.category}
                  </p>
                )}
                <p className="mt-1 font-display text-lg sm:text-xl text-navy leading-snug group-hover:text-gold transition-colors">
                  {plan.title}
                </p>
                {plan.subtitle && (
                  <p className="mt-1.5 text-sm text-navy/70 leading-relaxed line-clamp-2">
                    {plan.subtitle}
                  </p>
                )}
                {plan.duration_days > 0 && (
                  <p className="mt-2 text-[11px] uppercase tracking-widest text-gold">
                    {plan.duration_days} {plan.duration_days === 1 ? "dia" : "dias"}
                  </p>
                )}
              </Link>
            </Section>
          )}

          {/* Fase 2 (iter58) — Calendário Pastoral: seções defensivas.
              Cada uma só renderiza se a landing publicar o campo. */}

          {todosOsDias.length > 0 && (
            <Section title="Devocionais da semana" icon={CalendarDays} testid="semana-todos-dias-section">
              <ul className="space-y-2" data-testid="semana-todos-dias-list">
                {todosOsDias.slice(0, 7).map((d, i) => {
                  const dTitle = (d?.title || d?.devocional_title || d?.tema || "").trim();
                  const dVerse = (d?.verse_reference || d?.verse_ref || d?.versiculo || "").trim();
                  const dDate = (d?.date || d?.data || "").trim();
                  const isToday = dDate && todayIso && dDate === todayIso;
                  if (!dTitle && !dVerse) return null;
                  return (
                    <li
                      key={i}
                      data-testid={`semana-day-${i + 1}`}
                      data-today={isToday ? "true" : "false"}
                      className={`rounded-xl p-4 border transition-colors ${
                        isToday
                          ? "border-gold bg-gold/10"
                          : "border-border bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[10px] uppercase tracking-widest text-navy/50">
                          Dia {i + 1}{isToday ? " · Hoje" : ""}
                        </p>
                        {dVerse && (
                          <p className="text-[11px] text-gold uppercase tracking-widest">
                            {dVerse}
                          </p>
                        )}
                      </div>
                      {dTitle && (
                        <p className={`mt-1 font-display leading-snug ${isToday ? "text-navy text-lg" : "text-navy/90"}`}>
                          {dTitle}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {versiculosMemoria.length > 0 && (
            <Section title="Versículos para memorizar" icon={Bookmark} testid="semana-versiculos-memoria-section">
              <ul className="space-y-3" data-testid="semana-versiculos-memoria-list">
                {versiculosMemoria.slice(0, 3).map((v, i) => {
                  const vRef = (v?.reference || v?.verse_reference || v?.ref || "").trim();
                  const vText = (v?.text || v?.verse_text || (typeof v === "string" ? v : "")).trim();
                  if (!vRef && !vText) return null;
                  return (
                    <li
                      key={i}
                      data-testid={`semana-versiculo-memoria-${i + 1}`}
                      className="rounded-xl bg-[hsl(var(--parchment))] border border-navy/10 p-4"
                    >
                      {vText && (
                        <p className="font-display text-navy/90 leading-relaxed italic">
                          &ldquo;{vText}&rdquo;
                        </p>
                      )}
                      {vRef && (
                        <p className="mt-1.5 text-[11px] text-gold uppercase tracking-widest">
                          {vRef}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {acaoMissionaria && (
            <Section title="Desafio da semana" icon={Target} testid="semana-desafio-section">
              <div
                data-testid="semana-desafio-text"
                className="rounded-2xl bg-navy text-white/95 p-5 sm:p-6 leading-relaxed"
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">
                  Ação missionária
                </p>
                <p className="text-[15px]">{acaoMissionaria}</p>
              </div>
            </Section>
          )}

          {perguntasGrupo.length > 0 && (
            <Section title="Perguntas para seu grupo" icon={Users} testid="semana-perguntas-grupo-section">
              <ul className="space-y-2" data-testid="semana-perguntas-grupo-list">
                {perguntasGrupo.slice(0, 5).map((q, i) => {
                  const txt = (typeof q === "string" ? q : q?.text || q?.pergunta || "").trim();
                  if (!txt) return null;
                  return (
                    <li
                      key={i}
                      data-testid={`semana-pergunta-grupo-${i + 1}`}
                      className="flex gap-3 rounded-xl bg-white border border-border p-4"
                    >
                      <span className="text-gold font-display text-lg leading-none">{i + 1}.</span>
                      <p className="text-navy/85 leading-relaxed text-[15px]">{txt}</p>
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {/* Os sete dias da semana temática (contrato v1.1, blocos pastorais inline) */}
          <WeeklyThemeDaysList slug={wt?.theme_slug} />

          {/* CTA principal */}
          <div className="mt-10 text-center">
            <Link
              to="/devocional"
              data-testid="semana-cta-devocional"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-white hover:bg-gold hover:text-navy px-6 py-3 text-[11px] uppercase tracking-widest transition-colors"
            >
              Começar o devocional de hoje <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
